'use client'

import { useTranslations } from 'next-intl'

import { useClubFans } from '@/features/clubs/hooks'
import type { FanEntry } from '@/features/clubs/types'
import Image from '@/ui/MediaImage'
import { shouldUnoptimizeImageSrc } from '@/utils/imageUrl'

import { formatStatValue } from '../../formatStatValue'
import S from './TopFansCard.module.scss'

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    className={className}
  >
    <g opacity="0.6">
      <path
        d="M7.5 4.16602L13.3333 9.99935L7.5 15.8327"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
)

// Pedestal renders ranks in 4-2-1-3-5 order so the gold/silver podium sits
// in the middle. Empty slots fall back to a muted placeholder to keep the
// layout stable while data loads or for clubs with fewer than 5 fans.
const SLOT_LAYOUT: Array<{
  rank: number
  frame: string
  text: string
  pedestal: string
  pedestalText: string
}> = [
  {
    rank: 4,
    frame: S.avatarMuted,
    text: S.textMuted,
    pedestal: S.pedestalItemThin,
    pedestalText: S.pedestalTextMuted
  },
  {
    rank: 2,
    frame: S.avatarSilver,
    text: S.textSilver,
    pedestal: S.pedestalItemSilver,
    pedestalText: S.pedestalTextSilver
  },
  {
    rank: 1,
    frame: S.avatarGold,
    text: S.textGold,
    pedestal: S.pedestalItemGold,
    pedestalText: S.pedestalTextGold
  },
  {
    rank: 3,
    frame: S.avatarBronze,
    text: S.textBronze,
    pedestal: S.pedestalItemBronze,
    pedestalText: S.pedestalTextBronze
  },
  {
    rank: 5,
    frame: S.avatarMuted,
    text: S.textMuted,
    pedestal: S.pedestalItemThin,
    pedestalText: S.pedestalTextMuted
  }
]

interface TopFansCardProps {
  clubId?: string
  onViewLeaderboard?: () => void
  /** Link text; «Club leaderboard» by default. */
  linkLabel?: string
}

const TopFansCard = ({
  clubId,
  onViewLeaderboard,
  linkLabel
}: TopFansCardProps) => {
  const t = useTranslations('club.stats')
  const { data } = useClubFans(clubId, 5)
  const items: FanEntry[] = data?.pages[0]?.items ?? []
  const byRank = new Map(items.map(item => [item.rank, item] as const))

  return (
    <section className={S.card} aria-label={t('top5Fans')}>
      <div className={S.content}>
        <header className={S.header}>
          <span className={S.title}>{t('top5Fans')}</span>
          {onViewLeaderboard && (
            <button
              type="button"
              onClick={onViewLeaderboard}
              className={S.link}
            >
              <span className={S.linkText}>
                {linkLabel ?? t('clubLeaderboard')}
              </span>
              <ChevronRightIcon className={S.linkIcon} />
            </button>
          )}
        </header>

        <div className={S.pedestal}>
          {SLOT_LAYOUT.map(slot => {
            const fan = byRank.get(slot.rank)
            const name = fan?.nickname ?? '—'
            const value = formatStatValue(fan?.total_views)
            return (
              <div className={S.slot} key={slot.rank}>
                <div className={`${S.avatarFrame} ${slot.frame}`}>
                  {fan?.avatar_url ? (
                    <Image
                      src={fan.avatar_url}
                      alt={name}
                      width={76}
                      height={76}
                      className={S.avatarImg}
                      unoptimized={shouldUnoptimizeImageSrc(fan.avatar_url)}
                    />
                  ) : (
                    <span className={S.avatarPlaceholder} aria-hidden>
                      —
                    </span>
                  )}
                </div>
                <span className={`${S.name} ${slot.text}`} title={name}>
                  {name}
                </span>
                <span className={`${S.value} ${slot.text}`}>{value}</span>
                <div className={`${S.pedestalItem} ${slot.pedestal}`}>
                  <span className={slot.pedestalText}>{slot.rank}</span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default TopFansCard
